/**
 * Login page component
 */

import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Button, Input } from '../components/common';
import './Login.css';

/**
 * Login page with authentication form
 * @returns {React.ReactElement} Login page element
 */
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);

  const { signIn, resetPassword } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.message?.toLowerCase().includes('banned')
        ? 'User Account is currently Disabled'
        : error.message || 'Invalid email or password';
      addToast({
        type: 'error',
        message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      addToast({
        type: 'error',
        message: 'Please enter your email address'
      });
      return;
    }

    setLoading(true);
    try {
      await resetPassword(email);
      addToast({
        type: 'success',
        message: 'Password reset email sent. Check your inbox.'
      });
      setShowResetForm(false);
    } catch (error) {
      addToast({
        type: 'error',
        message: error.message || 'Failed to send reset email'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__header">
          <img src="/favicon.svg" alt="Logo" className="login-card__logo" />
          <h1 className="login-card__title">My Whiskey Collection</h1>
          <p className="login-card__subtitle">
            {showResetForm ? 'Reset your password' : 'Sign in to your account'}
          </p>
        </div>

        {showResetForm ? (
          <form onSubmit={handleResetPassword} className="login-card__form">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
            <Button type="submit" fullWidth loading={loading}>
              Send Reset Link
            </Button>
            <button
              type="button"
              className="login-card__link"
              onClick={() => setShowResetForm(false)}
            >
              Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="login-card__form">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              autoComplete="email"
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
            <Button type="submit" fullWidth loading={loading}>
              Sign In
            </Button>
            <button
              type="button"
              className="login-card__link"
              onClick={() => setShowResetForm(true)}
            >
              Forgot your password?
            </button>
          </form>
        )}


      </div>
    </div>
  );
}
